import render from 'posthtml-render';
import { BACKEND_ENUM_BY_HTML_REL_ATTRIBUTE_VALUE, REL_ATTRIBUTE_BACKEND_ENUM_BY_LEGACY_HTML_REL_ATTRIBUTE, BASE_REL_ATTRIBUTE_BACKEND_VALUES, REL_ATTRIBUTE_BACKEND_ENUM, HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM, SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING, IMAGE_SETTINGS, HTML_TAG, IMAGE_ALIGNMENT, TARGET, SPECIAL_CHAR, CUSTOM_HTML_TAG_VALUES, REMOVE_EMPTY_TAGS_EXCLUSION_TAG_LIST, INLINE_HTML_TAGS, INLINE_EXCLUSION_ELEMENTS_TO_UNWRAP_FROM_PARAGRAPH, REPLACE_DIV_WITH_PARAGRAPH_EXCLUSION_PARENT_TAG_LIST, CUSTOM_HTML_TAG, COMMENTED_HTML_REGEXP, EMPTY_TEXT_CHARS, WRAP_INLINE_ELEMENTS_TO_PARAGRAPH_EXCLUSION_TAG_LIST, CHARS_TO_SPLIT_PARAGRAPHS, REMOVE_INLINE_STYLES_EXCLUSION_TAG_LIST, TABLE_INSIDE_ARTICLE_DATA_ATTRIBUTE, CHARS_TO_ESCAPE, TWITTER_CARD_CLASS_NAME, TWITTER_CARD_SCRIPT_SOURCE } from '@fmpedia/enums';
import parser from 'posthtml-parser';
import { isArray, isObject, isString, generateParsedDomElementsHandlerDeep, checkIfFunction, getBinaryStringFromBooleanArray, hasParentInDataStructureDeep } from '@fmpedia/helpers';
import { pipe, omit, pathOr } from 'ramda';
import htmlTags from 'html-tags';
import { decodeHTML } from 'entities';

const LESS_THAN_SIGN_REGEXP = /<[^<]*/gim;
const LESS_THAN_SIGN_SHORTCODE = '[#lessthansign]';

function replaceLessThanSignWithShortcode(html) {
  return html.replace(LESS_THAN_SIGN_REGEXP, (match) => {
    if (match.includes('>')) return match

    return match.replace('<', LESS_THAN_SIGN_SHORTCODE)
  })
}

function replaceShortcodeWithSymbol({ html, shortcode, symbol }) {
  if (!html || !html.replace) return html

  return html.replace(shortcode, symbol)
}

function replaceShortcodeWithLessThanSign(html) {
  return replaceShortcodeWithSymbol({
    html,
    shortcode: LESS_THAN_SIGN_SHORTCODE,
    symbol: '<'
  })
}

function decodeShortcodesInParsedHtmlDeep(parsedHtml) {
  if (isArray(parsedHtml)) {
    return parsedHtml.map((el) => {
      if (!el) return el

      if (el.content) {
        return {
          ...el,
          content: decodeShortcodesInParsedHtmlDeep(el.content)
        }
      }

      if (!el.tag) {
        return replaceShortcodeWithLessThanSign(el)
      }

      return el
    })
  }

  if (parsedHtml.content) {
    return {
      ...parsedHtml,
      content: decodeShortcodesInParsedHtmlDeep(parsedHtml.content)
    }
  }

  if (!parsedHtml.tag) {
    return replaceShortcodeWithLessThanSign(parsedHtml)
  }

  return parsedHtml
}

function parseHtml(html) {
  if (!html) return []

  const parsedHtml = parser(replaceLessThanSignWithShortcode(html));

  return decodeShortcodesInParsedHtmlDeep(parsedHtml)
}

class LinkRelAttributeConverter {
  /**
   * @param directives {string}
   */
  constructor(directives) {
    this._attributeValue = directives || '';
  }

  /**
   * Generates a rel string based on rel values array
   * @param relValues {array}
   * @return {string}
   */
  static generateRelString(relValues = []) {
    return relValues.join(' ')
  }

  /**
   * Generates backend enums string based html rel string
   * @param htmlRelString {string}
   * @return {string}
   */
  static generateBackendEnumsFromHtmlRelString(htmlRelString) {
    return (htmlRelString || '')
      .split(' ')
      .map(
        (rel) =>
          BACKEND_ENUM_BY_HTML_REL_ATTRIBUTE_VALUE[rel] ||
          REL_ATTRIBUTE_BACKEND_ENUM_BY_LEGACY_HTML_REL_ATTRIBUTE[rel]
      )
      .filter((rel) => rel)
      .join(',')
  }
  get backendEnums() {
    return this._attributeValue.split(',')
  }
  get backendEnumsWithBaseDirectives() {
    return [
      ...new Set([
        ...this._attributeValue.split(','),
        ...BASE_REL_ATTRIBUTE_BACKEND_VALUES
      ])
    ]
  }
  get isNofollow() {
    return this.backendEnums.includes(REL_ATTRIBUTE_BACKEND_ENUM.NOFOLLOW)
  }
  get htmlRelString() {
    const relValues = this.backendEnums
      .map((rel) => HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM[rel])
      .filter((rel) => rel);

    return LinkRelAttributeConverter.generateRelString(relValues)
  }
  get htmlRelStringWithBaseDirectives() {
    const relValues = this.backendEnumsWithBaseDirectives
      .map((rel) => HTML_REL_ATTRIBUTE_VALUE_BY_BACKEND_ENUM[rel])
      .filter((rel) => rel);

    return LinkRelAttributeConverter.generateRelString(relValues)
  }
}

const helper = {
  joinRegexpStrings(
    arrayOfRegexpStrings,
    valueHandler = (v) => v,
    delimiter = '|'
  ) {
    if (!arrayOfRegexpStrings || !Array.isArray(arrayOfRegexpStrings)) return ''

    return arrayOfRegexpStrings
      .map((regexpStrings) => `(${valueHandler(regexpStrings)})`)
      .join(delimiter)
  },
  isObject(val) {
    if (!val) return false

    return typeof val === 'object' && val.constructor === Object
  },
  isString(val) {
    return typeof val === 'string' || val instanceof String
  },
  removeDuplicates(array) {
    return [...new Set(array)]
  },
  removeEmpty(array) {
    if (!array || !Array.isArray(array)) return array

    return array.filter((v) => !!v)
  }
};

function generateImageSizeClasses(startWidth, endWidth, widthStep) {
  const classes = [];
  let currentMinWidth = startWidth;

  while (currentMinWidth <= endWidth) {
    const currentMaxWidth = Math.min(currentMinWidth + widthStep - 1, endWidth);
    const className = `size-${currentMinWidth}-${currentMaxWidth}`;
    classes.push({
      minWidth: currentMinWidth,
      maxWidth: currentMaxWidth,
      className
    });
    currentMinWidth += widthStep;
  }

  return classes
}

const IMAGE_SIZE_CLASS_BY_WIDTH_RANGE = generateImageSizeClasses(1, 767, 50);

function getImageClassByWidth(imageWidth) {
  const match = IMAGE_SIZE_CLASS_BY_WIDTH_RANGE.find(
    ({ minWidth, maxWidth }) => imageWidth >= minWidth && imageWidth <= maxWidth
  );

  if (!match) return null

  return match.className
}

/**
 * We have multiple cases of image layout inside html. We have to handle
 * all this cases to properly replace image html with our implementation.
 */
const IMAGE_HTML_LAYOUT_REGEXP_STRINGS = [];

IMAGE_HTML_LAYOUT_REGEXP_STRINGS[0] = [
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.CAPTION_OPEN,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.ANY_CONTENT_LAZY,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.CAPTION_CLOSE
];
IMAGE_HTML_LAYOUT_REGEXP_STRINGS[1] = [
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.FIGURE_OPEN,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.ANY_CONTENT_LAZY,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.FIGURE_CLOSE
];
IMAGE_HTML_LAYOUT_REGEXP_STRINGS[2] = [
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.LINK_OPEN,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.ANY_CONTENT_LAZY,
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.LINK_CLOSE
];
IMAGE_HTML_LAYOUT_REGEXP_STRINGS[3] = [
  SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING.IMG
];

const IMAGE_MATCH_REGEXP_STRING = helper.joinRegexpStrings(
  IMAGE_HTML_LAYOUT_REGEXP_STRINGS,
  (v) => v.join('')
);
const IMAGE_MATCH_REGEXP = new RegExp(IMAGE_MATCH_REGEXP_STRING, 'gmis');

class ImageHtml {
  constructor(sourceData, settings = {}) {
    this._debug = settings.debug || false;
    this._addSizeClass = settings.addSizeClass || false;
    this._imageSettings = settings.imageSettings || IMAGE_SETTINGS;
    this._prettifyFn = settings.prettifyFn || null;

    if (!sourceData) {
      console.error('No source data provided for ImageHtml');
      return
    }
    if (!this._imageSettings) {
      console.error('No image settings provided for ImageHtml');
      return
    }

    this.console('== ImageHtml class ==');
    this.console('sourceHtml: ', sourceData);
    this._sourceHtml = helper.isString(sourceData) ? sourceData : null;
    this._sourceObject = helper.isObject(sourceData) ? sourceData : null;

    this._openLegacyCaptionShortcode = null;
    this._legacyCaptionHtml = null;
    this._htmlForParser = null;

    this._parsedHtml = null;
    this._figureTag = null;
    this._figcaptionTag = null;
    this._imgTag = null;
    this._linkTag = null;
    this._srcsetLocation = settings.srcsetLocation || null;

    this._customImageTagName = settings.customImageTagName || null;
    this._customAltText = null;
    this._customCaption = null;
    this._customAlign = null;
    this._customAspectRatio =
      settings.aspectRatio != null ? settings.aspectRatio : null;

    this._imageModifierFn = settings.imageModifierFn || null;

    if (this._sourceObject) {
      this.console('this: ', this);
      return
    }

    this.parseLegacyCaption();
    this.parseImageHtml();
    this.parseImageTags();

    this.console('this: ', this);
  }
  console(...args) {
    if (this._debug) {
      console.log(...args);
    }
  }
  /**
   * Parsing legacy caption
   */
  parseLegacyCaption() {
    const { ANY_CONTENT_LAZY, CAPTION_OPEN, CAPTION_CLOSE } =
      SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING;

    const searchRegexpString = helper.joinRegexpStrings(
      [CAPTION_OPEN, ANY_CONTENT_LAZY, CAPTION_CLOSE],
      (v) => v,
      ''
    );
    const searchRegexp = new RegExp(searchRegexpString, 'gmis');
    this._isLegacyCaptionExist = !!this._sourceHtml.match(searchRegexp);

    if (!this._isLegacyCaptionExist) {
      this._htmlForParser = this._sourceHtml;
      return
    }

    this._sourceHtml.replace(searchRegexp, (...args) => {
      this._openLegacyCaptionShortcode = args[1];
      const contentInsideLegacyCaptionShortcode = args[2];
      const { CAPTION_CONTENT } = SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING;
      const regexp = new RegExp(CAPTION_CONTENT, 'gmis');
      contentInsideLegacyCaptionShortcode.replace(regexp, (...args) => {
        this._htmlForParser = args[1] || null;
        this._legacyCaptionHtml = args[2] || null;
      });
      this._htmlForParser = this._htmlForParser || args[2];
    });
  }
  get legacyCaptionContent() {
    if (!this._legacyCaptionHtml) return null

    return parseHtml(
      this._prettifyFn
        ? this._prettifyFn(this._legacyCaptionHtml)
        : this._legacyCaptionHtml
    )
  }
  get legacyCaptionAlignValue() {
    if (!this._openLegacyCaptionShortcode) return null

    const { CAPTION_ALIGN } = SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING;
    const captionAlignRegexp = new RegExp(CAPTION_ALIGN, 'gmis');

    let alignValue = null;
    this._openLegacyCaptionShortcode.replace(captionAlignRegexp, (...args) => {
      const firstGroup = args[1];
      alignValue = firstGroup || null;
    });

    return alignValue
  }
  get alignFromLegacyCaption() {
    if (!this.legacyCaptionAlignValue || !this._imageSettings) return null

    const { ALIGNMENT_BY_LEGACY_CAPTION_ALIGN_VALUE } =
      this._imageSettings.ALIGNMENT;

    return (
      ALIGNMENT_BY_LEGACY_CAPTION_ALIGN_VALUE[this.legacyCaptionAlignValue] ||
      null
    )
  }
  parseImageHtml() {
    this._parsedHtml = parseHtml(this._htmlForParser);
  }
  /**
   * Parsing figure tag
   */
  parseFigureTag() {
    this._figureTag = this.getFigureTagFromElements(this._parsedHtml);
  }
  checkIfContentArrayExist(element) {
    if (!element) return false

    return element.content && Array.isArray(element.content)
  }
  getFigureTagFromElements(elements) {
    let figureTag = elements.find((el) => el.tag === HTML_TAG.FIGURE) || null;

    if (figureTag) return figureTag

    elements.forEach((element) => {
      if (figureTag || !this.checkIfContentArrayExist(element)) {
        return
      }

      figureTag = this.getFigureTagFromElements(element.content) || null;
    });

    return figureTag
  }
  getNonImageFigureContent() {
    if (!this.checkIfContentArrayExist(this._figureTag)) {
      return null
    }

    return this._figureTag.content.filter(
      this.checkIsNotImageRelatedEl.bind(this)
    )
  }
  checkIsNotImageRelatedEl(el) {
    if (!el || !el.tag || (el.trim && !el.trim())) return true

    const imageRelatedTags = [
      HTML_TAG.IMAGE,
      HTML_TAG.LINK,
      HTML_TAG.FIGCAPTION
    ];
    if (imageRelatedTags.includes(el.tag)) {
      return false
    }

    if (!el.content || !Array.isArray(el.content)) {
      return true
    }

    return el.content.every((child) => this.checkIsNotImageRelatedEl(child))
  }
  get originalFigureClassesString() {
    if (!this._figureTag) return null

    return (this._figureTag.attrs && this._figureTag.attrs.class) || null
  }
  get originalFigureClassesWithoutAlignment() {
    if (!this.originalFigureClassesString) return null

    const { FIGURE_CLASS_BY_ALIGNMENT } = this._imageSettings.ALIGNMENT;

    const alignmentClasses = Object.values(FIGURE_CLASS_BY_ALIGNMENT);

    return this.originalFigureClassesString
      .split(' ')
      .filter((className) => !alignmentClasses.includes(className))
  }
  get figureClasses() {
    if (!this._imageSettings) return []

    const { FIGURE } = this._imageSettings.CLASS_NAMES;

    const sizeClassName = this._addSizeClass
      ? getImageClassByWidth(+this.imageWidth)
      : null;

    return helper.removeDuplicates(
      helper.removeEmpty([
        FIGURE,
        this.alignClass,
        ...(this.originalFigureClassesWithoutAlignment || []),
        sizeClassName
      ])
    )
  }
  get dataMediaId() {
    if (this._sourceObject) {
      return this._sourceObject.dataMediaId || null
    }

    if (!this._imageSettings || !this._figureTag) return null

    const { DATA_MEDIA_ID } = this._imageSettings.ATTRIBUTES;

    return (
      (this._figureTag.attrs && this._figureTag.attrs[DATA_MEDIA_ID]) || null
    )
  }
  getFigureAlignmentByClassList(classList) {
    if (!classList || !this._imageSettings) return null

    const CLASS_BY_ALIGNMENT =
      this._imageSettings.ALIGNMENT.FIGURE_CLASS_BY_ALIGNMENT;

    if (classList.includes(CLASS_BY_ALIGNMENT[IMAGE_ALIGNMENT.LEFT])) {
      return IMAGE_ALIGNMENT.LEFT
    }

    if (classList.includes(CLASS_BY_ALIGNMENT[IMAGE_ALIGNMENT.RIGHT])) {
      return IMAGE_ALIGNMENT.RIGHT
    }

    if (classList.includes(CLASS_BY_ALIGNMENT[IMAGE_ALIGNMENT.CENTER])) {
      return IMAGE_ALIGNMENT.CENTER
    }

    return null
  }
  get alignFromFigureTag() {
    if (!this._figureTag) return null

    return this.getFigureAlignmentByClassList(this.originalFigureClassesString)
  }
  get figureAttrs() {
    if (!this._imageSettings) return null

    const { DATA_MEDIA_ID } = this._imageSettings.ATTRIBUTES;

    return {
      class: this.figureClasses.join(' '),
      [DATA_MEDIA_ID]: this.dataMediaId
    }
  }
  /**
   * Parsing link tag
   */
  parseLinkTag() {
    this._linkTag = this.getLinkTagFromElements(this._parsedHtml);
  }
  getLinkTagFromElements(elements) {
    let linkTag = elements.find((el) => el.tag === HTML_TAG.LINK) || null;

    if (!linkTag) {
      elements.forEach((element) => {
        if (!linkTag && element.content && Array.isArray(element.content)) {
          linkTag = this.getLinkTagFromElements(element.content);
        }
      });
    }

    return linkTag
  }
  get linkHref() {
    if (this._sourceObject) {
      return (this._sourceObject.link && this._sourceObject.link.href) || null
    }

    if (!this._linkTag) return null

    return (this._linkTag.attrs && this._linkTag.attrs.href) || null
  }
  get linkTarget() {
    if (this._sourceObject) {
      return (this._sourceObject.link && this._sourceObject.link.target) || null
    }

    if (!this._linkTag) return null

    return (this._linkTag.attrs && this._linkTag.attrs.target) || null
  }
  get linkRel() {
    if (this._sourceObject) {
      return (this._sourceObject.link && this._sourceObject.link.rel) || null
    }

    if (!this._linkTag) return null

    return (this._linkTag.attrs && this._linkTag.attrs.rel) || null
  }
  /**
   * Parsing figcaption tag
   */
  parseFigcaptionTag() {
    this._figcaptionTag = this.getFigcaptionTagFromElements(this._parsedHtml);
  }
  getFigcaptionTagFromElements(elements) {
    let figcaptionTag =
      elements.find((el) => el.tag === HTML_TAG.FIGCAPTION) || null;

    if (!figcaptionTag) {
      elements.forEach((element) => {
        if (
          !figcaptionTag &&
          element.content &&
          Array.isArray(element.content)
        ) {
          figcaptionTag = this.getFigcaptionTagFromElements(element.content);
        }
      });
    }

    return figcaptionTag
  }
  get figcaptionContentDataDiv() {
    if (
      !this._imageSettings ||
      !this._figcaptionTag ||
      !this._figcaptionTag.content ||
      !this._figcaptionTag.content.length
    ) {
      return null
    }

    const { FIGCAPTION_CONTENT } = this._imageSettings.CLASS_NAMES;

    return (
      this._figcaptionTag.content.find(
        (element) =>
          element.tag === HTML_TAG.DIV &&
          element.attrs &&
          element.attrs.class &&
          element.attrs.class.includes(FIGCAPTION_CONTENT)
      ) || null
    )
  }
  get figcaptionContentDataDivContent() {
    if (!this.figcaptionContentDataDiv) return null

    return this.figcaptionContentDataDiv.content
  }
  get figcaptionContent() {
    if (!this._figcaptionTag) return null

    if (this.figcaptionContentDataDivContent) {
      return this.figcaptionContentDataDivContent
    }

    const figcaptionContent = this._figcaptionTag.content;

    if (!figcaptionContent) return null

    return figcaptionContent
      .reduce((acc, figcaptionContentEl) => {
        if (
          figcaptionContentEl.tag &&
          figcaptionContentEl.content &&
          Array.isArray(figcaptionContentEl.content)
        ) {
          return [...acc, ...figcaptionContentEl.content]
        }
        return [...acc, figcaptionContentEl]
      }, [])
      .filter((v) => !!v && !!v.trim && !!v.trim())
  }
  get originalFigcaptionClasses() {
    if (!this._figcaptionTag) return null

    const figcaptionClassesString =
      this._figcaptionTag.attrs && this._figcaptionTag.attrs.class;

    if (!figcaptionClassesString) return null

    return figcaptionClassesString.split(' ')
  }
  get originalFigcaptionContentClasses() {
    if (
      !this._figcaptionTag ||
      !this._figcaptionTag.content ||
      !Array.isArray(this._figcaptionTag.content)
    ) {
      return null
    }

    const { FIGCAPTION_CONTENT } = this._imageSettings.CLASS_NAMES;

    const figcaptionContent = this._figcaptionTag.content.find((contentEl) => {
      return (
        contentEl.tag === HTML_TAG.DIV &&
        contentEl.attrs.class.includes(FIGCAPTION_CONTENT)
      )
    });

    if (!figcaptionContent) return null

    const figcaptionContentClassesString =
      figcaptionContent.attrs && figcaptionContent.attrs.class;

    if (!figcaptionContentClassesString) return null

    return figcaptionContentClassesString.split(' ')
  }
  get figcaptionClasses() {
    const { FIGCAPTION } = this._imageSettings.CLASS_NAMES;

    return helper.removeDuplicates(
      helper.removeEmpty([
        FIGCAPTION,
        ...(this.originalFigcaptionClasses || [])
      ])
    )
  }
  get figcaptionContentClasses() {
    const { FIGCAPTION_CONTENT } = this._imageSettings.CLASS_NAMES;

    return helper.removeDuplicates(
      helper.removeEmpty([
        FIGCAPTION_CONTENT,
        ...(this.originalFigcaptionContentClasses || [])
      ])
    )
  }
  /**
   * Parsing img tag
   */
  parseImgTag() {
    this._imgTag = this.getImgTagFromElements(this._parsedHtml);
  }
  getImgTagFromElements(elements) {
    let imgTag = elements.find((el) => el.tag === HTML_TAG.IMAGE) || null;

    if (!imgTag) {
      elements.forEach((element) => {
        if (!imgTag && element.content && Array.isArray(element.content)) {
          imgTag = this.getImgTagFromElements(element.content);
        }
      });
    }

    return imgTag
  }
  get imageSource() {
    if (this._sourceObject) {
      return this._sourceObject.source || null
    }

    if (!this._imgTag) return null

    return (this._imgTag.attrs && this._imgTag.attrs.src) || null
  }
  get imageAltText() {
    if (this._sourceObject) {
      return this._sourceObject.altText || null
    }

    if (!this._imgTag) return null

    return (this._imgTag.attrs && this._imgTag.attrs.alt) || null
  }
  set altText(newAltText) {
    this._customAltText = newAltText;
  }
  get originalImageClassesString() {
    if (!this._imgTag) return null

    return this._imgTag.attrs && this._imgTag.attrs.class
  }
  getImageAlignmentByLegacyClassesString(classList) {
    if (!classList || !this._imageSettings) return null

    const { LEGACY_IMAGE_CLASS_NAME_BY_ALIGNMENT } =
      this._imageSettings.ALIGNMENT;

    const legacyClassName = {
      left: LEGACY_IMAGE_CLASS_NAME_BY_ALIGNMENT[IMAGE_ALIGNMENT.LEFT],
      right: LEGACY_IMAGE_CLASS_NAME_BY_ALIGNMENT[IMAGE_ALIGNMENT.RIGHT],
      center: LEGACY_IMAGE_CLASS_NAME_BY_ALIGNMENT[IMAGE_ALIGNMENT.CENTER]
    };

    if (classList.includes(legacyClassName.left)) {
      return IMAGE_ALIGNMENT.LEFT
    }

    if (classList.includes(legacyClassName.right)) {
      return IMAGE_ALIGNMENT.RIGHT
    }

    if (classList.includes(legacyClassName.center)) {
      return IMAGE_ALIGNMENT.CENTER
    }

    return null
  }
  get imageStyle() {
    if (!this._imgTag) return null

    return (this._imgTag.attrs && this._imgTag.attrs.style) || null
  }
  get alignFromImageStyles() {
    if (!this.imageStyle) return null

    const { FLOAT_LEFT, FLOAT_RIGHT, MARGIN_LEFT_AUTO, MARGIN_RIGHT_AUTO } =
      this._imageSettings.STYLES;
    const isImageCentered =
      this.imageStyle.includes(MARGIN_LEFT_AUTO) &&
      this.imageStyle.includes(MARGIN_RIGHT_AUTO);

    if (this.imageStyle.includes(FLOAT_LEFT)) {
      return IMAGE_ALIGNMENT.LEFT
    } else if (this.imageStyle.includes(FLOAT_RIGHT)) {
      return IMAGE_ALIGNMENT.RIGHT
    } else if (isImageCentered) {
      return IMAGE_ALIGNMENT.CENTER
    }

    return null
  }
  get alignFromLegacyClassOfImgTag() {
    if (!this._imgTag) return null

    return this.getImageAlignmentByLegacyClassesString(
      this.originalImageClassesString
    )
  }
  get imageWidth() {
    if (this._sourceObject) {
      return this._sourceObject.width || null
    }

    if (!this._imgTag) return null

    return (this._imgTag.attrs && this._imgTag.attrs.width) || null
  }
  get imageHeight() {
    if (this._sourceObject) {
      return this._sourceObject.height || null
    }

    if (!this._imgTag) return null

    return (this._imgTag.attrs && this._imgTag.attrs.height) || null
  }
  get aspectRatio() {
    if (this._customAspectRatio != null) return this._customAspectRatio

    if (this._sourceObject) return this._sourceObject.aspectRatio

    if (!this._imgTag) return null

    const { ASPECT_RATIO } = this._imageSettings.ATTRIBUTES;

    return (this._imgTag.attrs && this._imgTag.attrs[ASPECT_RATIO]) || null
  }
  get isAspectRatioFlexible() {
    if (this.imageWidth && this.imageHeight) return false

    return !+this.aspectRatio
  }
  /**
   * Call all parsers
   */
  parseImageTags() {
    const parsers = [
      this.parseFigureTag.bind(this),
      this.parseFigcaptionTag.bind(this),
      this.parseLinkTag.bind(this),
      this.parseImgTag.bind(this)
    ];
    parsers.forEach((parser) => parser());
  }
  /**
   * Resulting Caption content
   */
  get captionContent() {
    if (this._customCaption != null) return this._customCaption

    if (this._sourceObject) {
      return this._sourceObject.caption ? [this._sourceObject.caption] : null
    }

    return this.figcaptionContent || this.legacyCaptionContent || null
  }
  set caption(newCaption) {
    this._customCaption = newCaption;
  }
  /**
   * Resulting image alignment
   */
  get alignment() {
    if (this._customAlign) return this._customAlign

    if (this._sourceObject) {
      return this._sourceObject.align || null
    }

    return (
      this.alignFromImageStyles ||
      this.alignFromFigureTag ||
      this.alignFromLegacyClassOfImgTag ||
      this.alignFromLegacyCaption ||
      null
    )
  }
  set alignment(newAlignValue) {
    this._customAlign = newAlignValue;
  }
  /**
   * Resulting align class
   */
  get alignClass() {
    if (!this.alignment || !this._imageSettings) return null

    const { FIGURE_CLASS_BY_ALIGNMENT } = this._imageSettings.ALIGNMENT;

    return FIGURE_CLASS_BY_ALIGNMENT[this.alignment] || null
  }
  /**
   * Resulting link object
   */
  get link() {
    if (!this.linkHref || this._customImageTagName) return null

    return {
      tag: HTML_TAG.LINK,
      attrs: {
        href: this.linkHref,
        target: this.linkTarget,
        rel: this.linkRel
      },
      content: [this.getImage()]
    }
  }
  /**
   * Resulting image object
   */
  getImage() {
    if (!this._imageSettings || !this.imageSource) return null

    const { WRAPPER_CLASS, DATA_SRC, ASPECT_RATIO, SRCSET_LOCATION } =
      this._imageSettings.ATTRIBUTES;
    const { IMAGE } = this._imageSettings.CLASS_NAMES;

    const alt =
      this._customAltText != null ? this._customAltText : this.imageAltText;
    const tag = this._customImageTagName || HTML_TAG.IMAGE;

    const width = this.imageWidth || null;
    const height = this.imageHeight || null;

    const baseAttrs = {
      src: this.imageSource,
      alt,
      width,
      height
    };

    const additionalAttrs = {
      [WRAPPER_CLASS]: IMAGE,
      [DATA_SRC]: this.imageSource,
      [ASPECT_RATIO]: this.aspectRatio,
      [SRCSET_LOCATION]: this._srcsetLocation
    };

    /**
     * Please note that we have to save boolean value for open-in-new-tab prop
     * as String because boolean value FALSE will remove the attribute during
     * the render.
     */
    const linkAttrs =
      this.linkHref && this._customImageTagName
        ? {
            url: this.linkHref,
            directives:
              LinkRelAttributeConverter.generateBackendEnumsFromHtmlRelString(
                this.linkRel
              ) || null,
            'open-in-new-tab': (this.linkTarget === TARGET.BLANK).toString()
          }
        : {};

    const image = {
      tag,
      attrs: {
        ...baseAttrs,
        ...additionalAttrs,
        ...linkAttrs
      }
    };

    try {
      return this.processImageByModifier(image)
    } catch (err) {
      this.console('Error while processing the image by modifier: ', err);
      return image
    }
  }
  processImageByModifier(image) {
    if (!this._imageModifierFn) return image

    return this._imageModifierFn({
      mediaId: this.dataMediaId,
      parsedImage: image
    })
  }
  /**
   * Resulting figcaption object
   */
  get figcaption() {
    if (!this.captionContent || !this._imageSettings) return null

    return {
      tag: HTML_TAG.FIGCAPTION,
      attrs: { class: this.figcaptionClasses.join(' ') },
      content: [
        {
          tag: HTML_TAG.DIV,
          attrs: { class: this.figcaptionContentClasses.join(' ') },
          content: this.captionContent
        }
      ]
    }
  }
  /**
   * Resulting figure content
   */
  getFigureContent() {
    return [this.link || this.getImage(), this.figcaption]
  }
  /**
   * Resulting figure object
   */
  getFigure() {
    return {
      tag: HTML_TAG.FIGURE,
      attrs: this.figureAttrs,
      content: this.getFigureContent()
    }
  }
  getFinalContentDataToRender() {
    return [this.getFigure(), ...(this.getNonImageFigureContent() || [])]
  }
  getFinalImageHtml() {
    return this.imageSource
      ? render(this.getFinalContentDataToRender())
      : this._sourceHtml
  }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var withParamsBrowser = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.withParams = void 0;

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var root = typeof window !== 'undefined' ? window : typeof commonjsGlobal !== 'undefined' ? commonjsGlobal : {};

var fakeWithParams = function fakeWithParams(paramsOrClosure, maybeValidator) {
  if (_typeof(paramsOrClosure) === 'object' && maybeValidator !== undefined) {
    return maybeValidator;
  }

  return paramsOrClosure(function () {});
};

var withParams = root.vuelidate ? root.vuelidate.withParams : fakeWithParams;
exports.withParams = withParams;
});

unwrapExports(withParamsBrowser);
var withParamsBrowser_1 = withParamsBrowser.withParams;

var params = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports._setTarget = void 0;
exports.popParams = popParams;
exports.pushParams = pushParams;
exports.target = void 0;
exports.withParams = withParams;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var stack = [];
var target = null;
exports.target = target;

var _setTarget = function _setTarget(x) {
  exports.target = target = x;
};

exports._setTarget = _setTarget;

function pushParams() {
  if (target !== null) {
    stack.push(target);
  }

  exports.target = target = {};
}

function popParams() {
  var lastTarget = target;
  var newTarget = exports.target = target = stack.pop() || null;

  if (newTarget) {
    if (!Array.isArray(newTarget.$sub)) {
      newTarget.$sub = [];
    }

    newTarget.$sub.push(lastTarget);
  }

  return lastTarget;
}

function addParams(params) {
  if (_typeof(params) === 'object' && !Array.isArray(params)) {
    exports.target = target = _objectSpread(_objectSpread({}, target), params);
  } else {
    throw new Error('params must be an object');
  }
}

function withParamsDirect(params, validator) {
  return withParamsClosure(function (add) {
    return function () {
      add(params);

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return validator.apply(this, args);
    };
  });
}

function withParamsClosure(closure) {
  var validator = closure(addParams);
  return function () {
    pushParams();

    try {
      for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
        args[_key2] = arguments[_key2];
      }

      return validator.apply(this, args);
    } finally {
      popParams();
    }
  };
}

function withParams(paramsOrClosure, maybeValidator) {
  if (_typeof(paramsOrClosure) === 'object' && maybeValidator !== undefined) {
    return withParamsDirect(paramsOrClosure, maybeValidator);
  }

  return withParamsClosure(paramsOrClosure);
}
});

unwrapExports(params);
var params_1 = params._setTarget;
var params_2 = params.popParams;
var params_3 = params.pushParams;
var params_4 = params.target;
var params_5 = params.withParams;

var withParams_1 = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var withParams = process.env.BUILD === 'web' ? withParamsBrowser.withParams : params.withParams;
var _default = withParams;
exports.default = _default;
});

unwrapExports(withParams_1);

var common = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.req = exports.regex = exports.ref = exports.len = void 0;
Object.defineProperty(exports, "withParams", {
  enumerable: true,
  get: function get() {
    return _withParams.default;
  }
});

var _withParams = _interopRequireDefault(withParams_1);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var req = function req(value) {
  if (Array.isArray(value)) return !!value.length;

  if (value === undefined || value === null) {
    return false;
  }

  if (value === false) {
    return true;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  if (_typeof(value) === 'object') {
    for (var _ in value) {
      return true;
    }

    return false;
  }

  return !!String(value).length;
};

exports.req = req;

var len = function len(value) {
  if (Array.isArray(value)) return value.length;

  if (_typeof(value) === 'object') {
    return Object.keys(value).length;
  }

  return String(value).length;
};

exports.len = len;

var ref = function ref(reference, vm, parentVm) {
  return typeof reference === 'function' ? reference.call(vm, parentVm) : parentVm[reference];
};

exports.ref = ref;

var regex = function regex(type, expr) {
  return (0, _withParams.default)({
    type: type
  }, function (value) {
    return !req(value) || expr.test(value);
  });
};

exports.regex = regex;
});

unwrapExports(common);
var common_1 = common.req;
var common_2 = common.regex;
var common_3 = common.ref;
var common_4 = common.len;

var email = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;



var emailRegex = /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;

var _default = (0, common.regex)('email', emailRegex);

exports.default = _default;
});

var email$1 = unwrapExports(email);

var validUrl = createCommonjsModule(function (module) {
(function(module) {

    module.exports.is_uri = is_iri;
    module.exports.is_http_uri = is_http_iri;
    module.exports.is_https_uri = is_https_iri;
    module.exports.is_web_uri = is_web_iri;
    // Create aliases
    module.exports.isUri = is_iri;
    module.exports.isHttpUri = is_http_iri;
    module.exports.isHttpsUri = is_https_iri;
    module.exports.isWebUri = is_web_iri;


    // private function
    // internal URI spitter method - direct from RFC 3986
    var splitUri = function(uri) {
        var splitted = uri.match(/(?:([^:\/?#]+):)?(?:\/\/([^\/?#]*))?([^?#]*)(?:\?([^#]*))?(?:#(.*))?/);
        return splitted;
    };

    function is_iri(value) {
        if (!value) {
            return;
        }

        // check for illegal characters
        if (/[^a-z0-9\:\/\?\#\[\]\@\!\$\&\'\(\)\*\+\,\;\=\.\-\_\~\%]/i.test(value)) return;

        // check for hex escapes that aren't complete
        if (/%[^0-9a-f]/i.test(value)) return;
        if (/%[0-9a-f](:?[^0-9a-f]|$)/i.test(value)) return;

        var splitted = [];
        var scheme = '';
        var authority = '';
        var path = '';
        var query = '';
        var fragment = '';
        var out = '';

        // from RFC 3986
        splitted = splitUri(value);
        scheme = splitted[1]; 
        authority = splitted[2];
        path = splitted[3];
        query = splitted[4];
        fragment = splitted[5];

        // scheme and path are required, though the path can be empty
        if (!(scheme && scheme.length && path.length >= 0)) return;

        // if authority is present, the path must be empty or begin with a /
        if (authority && authority.length) {
            if (!(path.length === 0 || /^\//.test(path))) return;
        } else {
            // if authority is not present, the path must not start with //
            if (/^\/\//.test(path)) return;
        }

        // scheme must begin with a letter, then consist of letters, digits, +, ., or -
        if (!/^[a-z][a-z0-9\+\-\.]*$/.test(scheme.toLowerCase()))  return;

        // re-assemble the URL per section 5.3 in RFC 3986
        out += scheme + ':';
        if (authority && authority.length) {
            out += '//' + authority;
        }

        out += path;

        if (query && query.length) {
            out += '?' + query;
        }

        if (fragment && fragment.length) {
            out += '#' + fragment;
        }

        return out;
    }

    function is_http_iri(value, allowHttps) {
        if (!is_iri(value)) {
            return;
        }

        var splitted = [];
        var scheme = '';
        var authority = '';
        var path = '';
        var port = '';
        var query = '';
        var fragment = '';
        var out = '';

        // from RFC 3986
        splitted = splitUri(value);
        scheme = splitted[1]; 
        authority = splitted[2];
        path = splitted[3];
        query = splitted[4];
        fragment = splitted[5];

        if (!scheme)  return;

        if(allowHttps) {
            if (scheme.toLowerCase() != 'https') return;
        } else {
            if (scheme.toLowerCase() != 'http') return;
        }

        // fully-qualified URIs must have an authority section that is
        // a valid host
        if (!authority) {
            return;
        }

        // enable port component
        if (/:(\d+)$/.test(authority)) {
            port = authority.match(/:(\d+)$/)[0];
            authority = authority.replace(/:\d+$/, '');
        }

        out += scheme + ':';
        out += '//' + authority;
        
        if (port) {
            out += port;
        }
        
        out += path;
        
        if(query && query.length){
            out += '?' + query;
        }

        if(fragment && fragment.length){
            out += '#' + fragment;
        }
        
        return out;
    }

    function is_https_iri(value) {
        return is_http_iri(value, true);
    }

    function is_web_iri(value) {
        return (is_http_iri(value) || is_https_iri(value));
    }

})(module);
});

const hrefValidation = {
  handledCase: {
    isStartFromMailto: 'isStartFromMailto',
    isStartFromProtocol: 'isStartFromProtocol',
    isStartFromDoubleSlash: 'isStartFromDoubleSlash',
    isStartFromSingleSlash: 'isStartFromSingleSlash',
    isStartFromHash: 'isStartFromHash'
  },
  mockedProtocol: 'https',
  mockedDomain: 'domain.com',
  get regexpStringByCase() {
    return {
      [this.handledCase.isStartFromMailto]: '^mailto:.+',
      [this.handledCase.isStartFromProtocol]: '^[\\w\\-_]+:\\/\\/.+',
      [this.handledCase.isStartFromDoubleSlash]: '^//[^/]',
      [this.handledCase.isStartFromSingleSlash]: '^/[^/]',
      [this.handledCase.isStartFromHash]: '^#[^#]?'
    }
  },
  get handlerByCase() {
    return {
      [this.handledCase.isStartFromMailto]:
        this.validateIfStartsWithMailto.bind(this),
      [this.handledCase.isStartFromProtocol]:
        this.validateIfStartsWithProtocol.bind(this),
      [this.handledCase.isStartFromDoubleSlash]:
        this.validateIfStartsWithDoubleSlash.bind(this),
      [this.handledCase.isStartFromSingleSlash]:
        this.validateIfStartsWithSingleSlash.bind(this),
      [this.handledCase.isStartFromHash]:
        this.validateIfStartsWithHash.bind(this)
    }
  },
  validateIfStartsWithMailto(href) {
    const emailAddress = href.match(/mailto:(.+)/i)[1];

    return email$1(emailAddress)
  },
  validateIfStartsWithProtocol(href) {
    return !!validUrl.isUri(href)
  },
  validateIfStartsWithDoubleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\/\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithSingleSlash(href) {
    const urlWithMockedProtocol = href.replace(
      /^\//i,
      `${this.mockedProtocol}://`
    );

    return this.validateIfStartsWithProtocol(urlWithMockedProtocol)
  },
  validateIfStartsWithHash(href) {
    const urlWithMockedPath = `${this.mockedProtocol}://${this.mockedDomain}${href}`;

    return this.validateIfStartsWithProtocol(urlWithMockedPath)
  },
  isHrefValid(href) {
    if (!href) return false

    const hrefCase = Object.values(this.handledCase).find(handledCase => {
      const caseRegexp = new RegExp(this.regexpStringByCase[handledCase], 'si');

      return href.match(caseRegexp)
    });

    if (!hrefCase) return false

    const handler = this.handlerByCase[hrefCase];

    try {
      return handler(href)
    } catch (err) {
      return false
    }
  }
};

function isHrefValid(href) {
  return hrefValidation.isHrefValid(href)
}

/**
 * We use fixAttrsInParsedHtml because we found a case when there might be a double
 * quote inside attr value.
 * Example:
 * <a href="#" target="_blank" rel="nofollow&quot; noopener">
 *
 * Without this fix js breaks when trying to render the tag with double quote
 * inside attribute value.
 * This fix removes all double quotes in attrs object values only.
 */
const doubleQuoteRegExp = new RegExp(SPECIAL_CHAR.DOUBLE_QUOTE, 'gi');
const FORBIDDEN_ATTRIBUTE_NAMES = ['-', ':', '_'];

function fixAttrsInParsedHtml(elements) {
  if (isArray(elements)) {
    return elements.map((element) => fixAttrsInParsedHtml(element))
  }

  if (isObject(elements)) {
    return {
      ...elements,
      attrs: fixAttrValues(elements.attrs),
      content: fixAttrsInParsedHtml(elements.content)
    }
  }

  return elements

  function fixAttrValues(attrs) {
    if (!isObject(attrs)) return attrs

    return Object.entries(attrs).reduce((acc, [attrName, attrValue]) => {
      const isAttrNameIncludesForbiddenCharacters =
        attrName && !!attrName.match(/[^a-z:\-_]/);

      const isForbiddenAttrName = FORBIDDEN_ATTRIBUTE_NAMES.some(
        (name) => attrName === name
      );

      const isTableInsideArticleAttr =
        attrName === TABLE_INSIDE_ARTICLE_DATA_ATTRIBUTE;

      /**
       * There is an FL article where for some reason, a snippet of text was
       * injected in place where declaration of HTML tag attributes is supposed
       * to be. (/news/!/forexlive-nth-american-session-wrap-21-oct-20151021)
       * Therefore, this article includes a lot of attributes with forbidden
       * symbols that broke JS.
       */
      const isAttrNameForbidden =
        (isAttrNameIncludesForbiddenCharacters || isForbiddenAttrName) &&
        !isTableInsideArticleAttr;

      if (isAttrNameForbidden) {
        return {
          ...acc
        }
      }

      const isAttrValueWithDoubleQuote =
        isString(attrValue) && attrValue.includes(SPECIAL_CHAR.DOUBLE_QUOTE);
      const isAttrNameWithDoubleQuote =
        isString(attrName) && attrName.includes(SPECIAL_CHAR.DOUBLE_QUOTE);

      const resultingAttrName = isAttrNameWithDoubleQuote
        ? attrName.replace(doubleQuoteRegExp, '')
        : attrName;

      const resultingAttrValue = isAttrValueWithDoubleQuote
        ? attrValue.replace(doubleQuoteRegExp, '')
        : attrValue;

      return {
        ...acc,
        [resultingAttrName]: resultingAttrValue
      }
    }, {})
  }
}

const FORBIDDEN_TAGS = [HTML_TAG.STYLE];

function removeForbiddenTags(elements) {
  if (isArray(elements)) {
    return elements.map((el) => removeForbiddenTags(el))
  }

  if (isObject(elements)) {
    if (FORBIDDEN_TAGS.includes(getTag(elements))) return null

    return {
      ...elements,
      content: removeForbiddenTags(elements.content)
    }
  }

  return elements
}

function getTag(el) {
  if (!el) return null

  return el.tag && isString(el.tag) ? el.tag.toLowerCase() : null
}

function isElementPureText(el) {
  return isString(el)
}

function isLineBreakTag(el) {
  return getTag(el) === HTML_TAG.LINE_BREAK
}

function escapeCharsInText(text) {
  if (!text) return text

  return text
    .split('')
    .map((char) => (CHARS_TO_ESCAPE.includes(char) ? `\\${char}` : char))
    .join('')
}

function removeEmptySymbolsFromText(text) {
  if (!text || !isString(text)) return text

  const emptySymbolsRegExpString = EMPTY_TEXT_CHARS.map(
    (symbol) => `(${escapeCharsInText(symbol)})`
  ).join('|');
  const regExp = new RegExp(emptySymbolsRegExpString, 'gmi');

  return text.replace(regExp, '')
}

function isEmptyText(text) {
  if (!text) return true

  if (isString(text)) {
    return !removeEmptySymbolsFromText(text).length
  }

  return false
}

function isScriptTag(el) {
  const tag = getTag(el);
  return [HTML_TAG.SCRIPT, CUSTOM_HTML_TAG.A_SCRIPT].includes(tag)
}

function isTagShouldBeExcluded(el, exclusionTagList) {
  const tag = getTag(el);
  return exclusionTagList.includes(tag)
}

function isEmptyTagDeep(el) {
  if (!el) return true

  if (isString(el)) {
    return isEmptyText(el.trim())
  }

  if (isTagShouldBeExcluded(el, REMOVE_EMPTY_TAGS_EXCLUSION_TAG_LIST)) {
    return false
  }

  if (isLineBreakTag(el) || !el.content) return true

  if (isString(el.content)) {
    return isEmptyText(el.content.trim())
  }

  if (isArray(el.content)) {
    return el.content.every((child) => isEmptyTagDeep(child))
  }

  return false
}

function wrapElementsToParagraph(el) {
  if (
    isArray(el) &&
    el.length === 1 &&
    isTagShouldBeExcluded(
      el[0],
      WRAP_INLINE_ELEMENTS_TO_PARAGRAPH_EXCLUSION_TAG_LIST
    )
  ) {
    return el[0]
  }

  return {
    tag: HTML_TAG.PARAGRAPH,
    content: isArray(el) ? el : [el]
  }
}

function isInlineElement(el) {
  return isElementPureText(el) || INLINE_HTML_TAGS.includes(getTag(el))
}

function isFirstOrLastChild({ acc, index }) {
  const isFirst = index === 0;
  const isLast = index === acc.length - 1;

  return isFirst || isLast
}

function isAllPreviousOrNextChildrenEmpty({ acc, index }) {
  const isAllPreviousEmpty = acc.slice(0, index).every((v) => v === null);
  const isAllNextEmpty = acc.slice(index + 1).every(isEmptyTagDeep);

  return isAllPreviousEmpty || isAllNextEmpty
}

function isPreviousAndNextChildInlineContent({ acc, index }) {
  const previousEl = acc[index - 1];
  const nextEl = acc[index + 1];

  const isPreviousElEmptyOrInline = !previousEl || !isInlineElement(previousEl);
  const isNextElEmptyOrInline =
    isEmptyTagDeep(nextEl) || !isInlineElement(nextEl);

  return isPreviousElEmptyOrInline && isNextElEmptyOrInline
}

const removeEmptyTagsDeepHandlerSettings = [
  {
    conditionFn: (el) => el && el.tag && isEmptyTagDeep(el),
    handlerFn: () => null
  },
  {
    conditionFn: (el) => {
      if (!el || !el.tag || !el.content) return false

      return el.content.some(isEmptyTagDeep)
    },
    handlerFn: (el) => {
      if (!el || !el.content) return null

      if (el.content.length <= 1) {
        return isEmptyTagDeep(el) ? null : el
      }
      const newChildren = el.content.reduce((acc, child, index) => {
        if (!isEmptyTagDeep(child)) return acc

        const childData = { acc, child, index };
        if (
          isFirstOrLastChild(childData) ||
          isAllPreviousOrNextChildrenEmpty(childData) ||
          isPreviousAndNextChildInlineContent(childData)
        ) {
          acc[index] = null;
        }

        return acc
      }, el.content);

      return {
        ...el,
        content: newChildren
      }
    }
  }
];

const removeEmptyTagsDeepHandler = generateParsedDomElementsHandlerDeep(
  removeEmptyTagsDeepHandlerSettings
);

function removeEmptyTags(elements) {
  return removeEmptyTagsDeepHandler(elements)
}

function isParentWithBrToRemove(el, brEl) {
  if (!el || !el.tag || !el.content) {
    return false
  }
  if (!el.content.includes(brEl)) {
    return false
  }

  const contentWithOnlyBr = el.content.filter((el) => {
    if (!el.tag || el.tag !== HTML_TAG.LINE_BREAK) {
      return true
    }

    return el === brEl
  });

  const childNumber = contentWithOnlyBr.length;
  const indexOfBr = contentWithOnlyBr.indexOf(brEl);
  const borderIndexes = [0, childNumber - 1];

  return borderIndexes.includes(indexOfBr)
}

const generateRemoveUnnecessaryBrHandlerSettings = (parsedContent) => [
  {
    conditionFn: (el) => {
      if (!el || !el.tag || el.tag !== HTML_TAG.LINE_BREAK) {
        return false
      }
      const brEl = el;

      return hasParentInDataStructureDeep({
        dataStructure: parsedContent,
        targetElement: el,
        parentConditionFn: (el) => isParentWithBrToRemove(el, brEl)
      })
    },
    handlerFn: () => null
  }
];

function removeUnnecessaryBrTagsDeep(elements) {
  const removeUnnecessaryBrHandlerSettings =
    generateRemoveUnnecessaryBrHandlerSettings(elements);
  const removeUnnecessaryBrHandler = generateParsedDomElementsHandlerDeep(
    removeUnnecessaryBrHandlerSettings
  );
  return removeUnnecessaryBrHandler(elements)
}

function wrapRootToParagraphs(elements) {
  if (!isArray(elements)) {
    return elements
  }

  const paragraphIndex = {
    start: null,
    end: null
  };
  function clearParagraphIndexes() {
    paragraphIndex.start = null;
    paragraphIndex.end = null;
  }
  function createParagraphChildren() {
    const children = elements.slice(
      paragraphIndex.start,
      paragraphIndex.end + 1
    );
    clearParagraphIndexes();
    return wrapElementsToParagraph(children)
  }

  return elements.reduce((acc, el, index) => {
    const isLastElement = index === elements.length - 1;
    const isStartIndexExist = paragraphIndex.start !== null;
    const mapValue = getBinaryStringFromBooleanArray([
      isLastElement,
      isInlineElement(el),
      isStartIndexExist
    ]);
    /**
     * [       x1                   x2                   x3          ]
     * "Is Last Element"   "Is Inline Element"  "Is Start Index Exist"
     */
    switch (mapValue) {
      case '000':
      case '100':
        return [...acc, el]
      case '001':
      case '101':
        paragraphIndex.end = index - 1;
        return [...acc, createParagraphChildren(), el]
      case '010':
        paragraphIndex.start = index;
        return acc
      case '011':
        return acc
      case '110':
        paragraphIndex.start = index;
        paragraphIndex.end = index;
        return [...acc, createParagraphChildren()]
      case '111':
        paragraphIndex.end = index;
        return [...acc, createParagraphChildren()]
    }
  }, [])
}

function unwrapInlineExclusionElementsFromParagraph(elements) {
  if (!isArray(elements)) {
    return elements
  }

  return elements.reduce((acc, el) => {
    if (
      getTag(el) === HTML_TAG.PARAGRAPH &&
      isArray(el.content) &&
      el.content.some((child) =>
        INLINE_EXCLUSION_ELEMENTS_TO_UNWRAP_FROM_PARAGRAPH.includes(child.tag)
      )
    ) {
      /**
       * We add next line before the unwrapped content to prevent
       * "joinPureTextElementsDeep" from joining text from outside
       * the unwrapped paragraph with the text inside of it.
       *
       * Example:
       * Before: "This text <p>shouldn't be joined with this text <iframe /></p>"
       * After processing: "<p>This text</p><p>shouldn't be joined with this text</p><iframe />"
       */
      return [...acc, SPECIAL_CHAR.NEXT_LINE, ...el.content]
    }

    return [...acc, el]
  }, [])
}

const unwrapContentFromTagsDeep = (isTagToUnwrapFn) =>
  function unwrapContentFromTagsInner(elements) {
    if (!isArray(elements) || !checkIfFunction(isTagToUnwrapFn)) {
      return elements
    }

    return elements.reduce((acc, el) => {
      if (!el) return acc

      if (!isObject(el)) {
        return [...acc, el]
      }

      const tag = getTag(el);

      const isTagToUnwrap = isTagToUnwrapFn(tag);

      if (isArray(el.content)) {
        return isTagToUnwrap
          ? [...acc, ...unwrapContentFromTagsInner(el.content)]
          : [
              ...acc,
              {
                ...el,
                content: unwrapContentFromTagsInner(el.content)
              }
            ]
      }

      return isTagToUnwrap ? [...acc, el.content] : [...acc, el]
    }, [])
  };

function hasChildLinkDeep(elements) {
  if (!isArray(elements)) {
    return false
  }

  return elements.some((el) => {
    if (getTag(el) === HTML_TAG.ANCHOR) return true

    if (el && isArray(el.content)) {
      return el.content.some(
        (childEl) =>
          childEl &&
          (getTag(childEl) === HTML_TAG.ANCHOR ||
            hasChildLinkDeep(childEl.content))
      )
    }

    return false
  })
}

/**
 * Link inside another link causes DOM mismatch errors:
 * https://adraba.atlassian.net/browse/FMP-14790
 * @param elements
 * @returns {*}
 */
function fixNestedLinksDeep(elements) {
  if (!isArray(elements)) {
    return elements
  }

  return elements.reduce((acc, el) => {
    if (!el) return acc

    if (!isObject(el)) {
      return [...acc, el]
    }

    if (getTag(el) === HTML_TAG.ANCHOR) {
      const isValidUrl = el.attrs && isHrefValid(el.attrs.href);

      const hasChildLink = hasChildLinkDeep(el.content);

      if (hasChildLink) {
        return [...acc, ...fixNestedLinksDeep(el.content || [])]
      }

      const targetEl = isValidUrl
        ? el
        : {
            tag: HTML_TAG.SPAN,
            content: el.content
          };

      return [...acc, targetEl]
    }

    return [
      ...acc,
      {
        ...el,
        ...(el.content ? { content: fixNestedLinksDeep(el.content) } : {})
      }
    ]
  }, [])
}

function joinPureTextElementsDeep(elements) {
  if (isElementPureText(elements)) {
    return elements
  }

  const childIndex = {
    start: null,
    end: null
  };

  return elements.reduce((acc, child, index) => {
    const isLastElement = index === elements.length - 1;
    const isPureText = isElementPureText(child);
    const isStartIndexExist = childIndex.start !== null;
    const mapValue = getBinaryStringFromBooleanArray([
      isLastElement,
      isPureText,
      isStartIndexExist
    ]);

    /**
     * [       x1                 x2                   x3        ]
     * "Is Last Element"   "Is Pure Text"   "Is Start Index Exist"
     */
    switch (mapValue) {
      case '000':
      case '100':
        return [
          ...acc,
          {
            ...child,
            ...(child.content
              ? { content: joinPureTextElementsDeep(child.content) }
              : {})
          }
        ]
      case '001':
      case '101':
        childIndex.end = index - 1;

        return [
          ...acc,
          createJoinedTextElements(elements),
          {
            ...child,
            ...(child.content
              ? { content: joinPureTextElementsDeep(child.content) }
              : {})
          }
        ]
      case '010':
        childIndex.start = index;
        return acc
      case '011':
        return acc
      case '110':
        return [...acc, child]
      case '111':
        childIndex.end = index;
        return [...acc, createJoinedTextElements(elements)]
    }
  }, [])

  function clearChildIndexes() {
    childIndex.start = null;
    childIndex.end = null;
  }
  function createJoinedTextElements(elements) {
    const children = elements.slice(childIndex.start, childIndex.end + 1);
    clearChildIndexes();
    return children.join('')
  }
}

function checkIfContainsTagDeep(el, tagName) {
  if (!el || !el.content) return false

  return el.content.some((child) => {
    const isChildTargetTag = child && child.tag && child.tag === tagName;
    return isChildTargetTag || checkIfContainsTagDeep(child, tagName)
  })
}

const generateConvertDivToParagraphHandlerSettings = (parsedContent) => [
  {
    conditionFn: (el) => {
      if (!el || !el.tag) return false

      const hasParentsToExclude = hasParentInDataStructureDeep({
        dataStructure: parsedContent,
        targetElement: el,
        parentConditionFn: (el) =>
          el &&
          el.tag &&
          REPLACE_DIV_WITH_PARAGRAPH_EXCLUSION_PARENT_TAG_LIST.includes(el.tag)
      });

      if (hasParentsToExclude) return false

      return el.tag === HTML_TAG.DIV
    },
    handlerFn: (el) => ({
      ...el,
      tag: HTML_TAG.PARAGRAPH
    })
  }
];

const flattenParagraphTagsDeepHandlerSettings = [
  {
    conditionFn: (el) => {
      if (!el || !el.tag || el.tag !== HTML_TAG.PARAGRAPH || !el.content) {
        return false
      }

      return checkIfContainsTagDeep(el, HTML_TAG.PARAGRAPH)
    },
    handlerFn: (el) =>
      el.content.reduce((acc, child) => {
        if (
          (child && child.tag === HTML_TAG.PARAGRAPH) ||
          checkIfContainsTagDeep(child, HTML_TAG.PARAGRAPH)
        ) {
          return [...acc, child]
        }

        return [...acc, { ...el, content: [child] }]
      }, [])
  }
];

const flattenParagraphTagsDeep = generateParsedDomElementsHandlerDeep(
  flattenParagraphTagsDeepHandlerSettings
);

function convertDivToParagraphDeep(elements) {
  const convertDivToParagraphDeepHandler = generateParsedDomElementsHandlerDeep(
    generateConvertDivToParagraphHandlerSettings(elements)
  );

  return convertDivToParagraphDeepHandler(elements)
}

function isNextLine(el) {
  if (!el || !isString(el)) return false

  const isContainLineFeed = el.includes(SPECIAL_CHAR.LINE_FEED);
  const regExp = new RegExp(`[${EMPTY_TEXT_CHARS.join('')}]+`, 'gmi');

  return isContainLineFeed && el.replace(regExp, '').length === 0
}

function splitTextElementByNextLine(text) {
  if (!isString(text)) return [text]

  const regExpString = CHARS_TO_SPLIT_PARAGRAPHS.map(
    (char) => `(${escapeCharsInText(char)})`
  ).join('|');
  const nextLineRegExp = new RegExp(regExpString, 'gmi');

  return text.split(nextLineRegExp)
}

function splitParagraphsByNextLine(elements) {
  if (!isArray(elements)) {
    return elements
  }

  let paragraphChildren = [];

  return elements.reduce((resultElementsAcc, child, index) => {
    if (isNextLine(child)) {
      if (paragraphChildren.length) {
        const wrappedChildren = wrapElementsToParagraph(paragraphChildren);
        paragraphChildren = [];
        return [...resultElementsAcc, wrappedChildren]
      }

      return resultElementsAcc
    }

    const isLastChild = index === elements.length - 1;

    if (isElementPureText(child)) {
      const splittedElements = splitTextElementByNextLine(child);
      const isNextLineFound = splittedElements.length > 1;

      if (isNextLineFound) {
        const paragraphs = wrapTextElementsIntoParagraphs(
          splittedElements,
          isLastChild
        );

        return [...resultElementsAcc, ...paragraphs]
      }

      if (!isEmptyText(child)) {
        paragraphChildren.push(child);
      }

      if (isLastChild && paragraphChildren.length) {
        const wrappedChildren = wrapElementsToParagraph(paragraphChildren);
        paragraphChildren = [];

        return [...resultElementsAcc, wrappedChildren]
      }

      return resultElementsAcc
    }

    if (isInlineElement(child)) {
      if (isScriptTag(child)) {
        return [...resultElementsAcc, child]
      }

      paragraphChildren.push(child);

      if (isLastChild) {
        const wrappedChildren = wrapElementsToParagraph(paragraphChildren);
        paragraphChildren = [];

        return [...resultElementsAcc, wrappedChildren]
      }

      return resultElementsAcc
    }

    if (paragraphChildren.length) {
      const wrappedChildren = wrapElementsToParagraph(paragraphChildren);
      paragraphChildren = [];

      return [...resultElementsAcc, wrappedChildren, child]
    }

    return [...resultElementsAcc, child]
  }, [])

  function wrapTextElementsIntoParagraphs(arrayOfTextElements, isLastChild) {
    return arrayOfTextElements.reduce((paragraphsAcc, paragraph, index) => {
      const isFirstParagraph = index === 0;
      const isLastParagraph = index === arrayOfTextElements.length - 1;

      if (!paragraph) {
        if (!paragraphChildren.length) return paragraphsAcc

        const wrappedChildren = wrapElementsToParagraph(paragraphChildren);
        paragraphChildren = [];
        return [...paragraphsAcc, wrappedChildren]
      }

      if (isFirstParagraph) {
        paragraphChildren.push(paragraph);
        const firstParagraph = wrapElementsToParagraph(paragraphChildren);
        paragraphChildren = [];
        return [...paragraphsAcc, firstParagraph]
      }

      if (isLastParagraph && !isLastChild) {
        paragraphChildren.push(paragraph);
        return paragraphsAcc
      }

      return [...paragraphsAcc, wrapElementsToParagraph(paragraph)]
    }, [])
  }
}

function replaceNonBreakingSpaces(html) {
  return html
    .replace(new RegExp(SPECIAL_CHAR.NON_BREAKING_SPACE, 'gmi'), ' ')
    .replace(new RegExp(SPECIAL_CHAR.NON_BREAKING_SPACE_TEXT, 'gmi'), ' ')
}

function replaceCommentedHtml(html) {
  return html.replace(COMMENTED_HTML_REGEXP, '')
}

function removeInlineStyles(el) {
  if (!el) return el

  if (el.attrs && el.attrs.style) {
    return { ...el, attrs: omit(['style'], el.attrs) }
  }

  return el
}

const INLINE_STYLE_HANDLERS = [
  (style) => style.replace(/!\s*important\s*;?/gm, '')
];

function handleInlineStyles(el) {
  if (!el) return el

  if (!isTagShouldBeExcluded(el, REMOVE_INLINE_STYLES_EXCLUSION_TAG_LIST)) {
    return removeInlineStyles(el)
  }

  if (el.attrs && el.attrs.style) {
    return {
      ...el,
      attrs: {
        ...el.attrs,
        style: pipe(...INLINE_STYLE_HANDLERS)(el.attrs.style)
      }
    }
  }

  return el
}

function handleInlineStylesDeep(elements) {
  if (!isArray(elements)) {
    return elements
  }

  return elements.map((el) => {
    const elWithHandledStyles = handleInlineStyles(el);

    if (el.content && isArray(el.content)) {
      const processedContent = handleInlineStylesDeep(el.content);

      return {
        ...elWithHandledStyles,
        content: processedContent
      }
    }

    return elWithHandledStyles
  })
}

function unwrapBlockElementsFromParent(parent) {
  if (!parent || !parent.content || !isArray(parent.content)) {
    return isArray(parent) ? parent : [parent]
  }

  const parentTag = getTag(parent);
  const newParentElements = [];
  let temporaryChildren = [];

  if (
    parentTag === HTML_TAG.PARAGRAPH ||
    INLINE_HTML_TAGS.includes(parentTag)
  ) {
    parent.content.forEach((child) => {
      const childTag = getTag(child);
      const isAllowed = !childTag || INLINE_HTML_TAGS.includes(childTag);

      if (isAllowed) {
        temporaryChildren.push(child);
      } else {
        if (temporaryChildren.length) {
          newParentElements.push({ ...parent, content: temporaryChildren });
          temporaryChildren = [];
        }
        newParentElements.push(child);
      }
    });

    if (temporaryChildren.length) {
      newParentElements.push({ ...parent, content: temporaryChildren });
    }
  } else {
    // If it's not a paragraph or inline element, just return the parent as is
    newParentElements.push(parent);
  }

  return newParentElements
}
/**
 * We unwrap block elements from paragraph as well since it's considered an
 * invalid HTML and causes DOM mismatch errors
 */
function unwrapBlockElementsFromInlineOrParagraphDeep(elements) {
  if (
    !elements ||
    (!isArray(elements) && (!isObject(elements) || !elements.content))
  ) {
    return elements
  }

  const calculationData = {
    newElements: [],
    newElementsBeforeProcessing: []
  };

  if (isObject(elements)) {
    calculationData.newElementsBeforeProcessing.push({
      ...elements,
      content: unwrapBlockElementsFromInlineOrParagraphDeep(elements.content)
    });
  } else {
    calculationData.newElementsBeforeProcessing.push(
      ...elements.reduce((acc, el) => {
        if (!el) return acc

        if (isArray(el.content)) {
          return [
            ...acc,
            {
              ...el,
              content: unwrapBlockElementsFromInlineOrParagraphDeep(el.content)
            }
          ]
        }

        return [...acc, el]
      }, [])
    );
  }

  calculationData.newElementsBeforeProcessing.forEach((el) => {
    if (!el && !el.content) return

    calculationData.newElements.push(...unwrapBlockElementsFromParent(el));
  });

  return calculationData.newElements
}

/**
 * Vector Markup Language (used in Word)
 * @param el
 * @return {*|boolean}
 */
function checkIsElementIsVmlTag(el) {
  if (!el || !el.tag) return false

  return el.tag.match(/^v:.+/)
}

function isInvalidTag(tag) {
  return !htmlTags.includes(tag)
}

function isCustomTag(tag) {
  return CUSTOM_HTML_TAG_VALUES.includes(tag)
}

const TAGS_TO_UNWRAP = [HTML_TAG.SPAN, HTML_TAG.FONT];

const PRETTIFY_OPTION = {
  DISABLE_RENDER: 'DISABLE_RENDER',
  DISABLE_WRAP_ROOT_TO_PARAGRAPHS: 'DISABLE_WRAP_ROOT_TO_PARAGRAPHS',
  DISABLE_SPLIT_PARAGRAPHS_BY_NEXT_LINE:
    'DISABLE_SPLIT_PARAGRAPHS_BY_NEXT_LINE',
  DISABLE_HANDLE_INLINE_STYLES: 'DISABLE_HANDLE_INLINE_STYLES'
};

function prettifyHtml(html, settings = {}) {
  try {
    if (!html) return html

    const isTagToUnwrapHandler = (tag) =>
      !isCustomTag(tag) && (TAGS_TO_UNWRAP.includes(tag) || isInvalidTag(tag));

    const handlerList = [
      replaceNonBreakingSpaces,
      replaceCommentedHtml,
      parseHtml,
      fixAttrsInParsedHtml,
      unwrapContentFromTagsDeep(isTagToUnwrapHandler),
      fixNestedLinksDeep,
      convertDivToParagraphDeep,
      unwrapBlockElementsFromInlineOrParagraphDeep,
      unwrapInlineExclusionElementsFromParagraph,
      ...(settings[PRETTIFY_OPTION.DISABLE_SPLIT_PARAGRAPHS_BY_NEXT_LINE]
        ? []
        : [splitParagraphsByNextLine]),
      flattenParagraphTagsDeep,
      joinPureTextElementsDeep,
      ...(settings[PRETTIFY_OPTION.DISABLE_WRAP_ROOT_TO_PARAGRAPHS]
        ? []
        : [wrapRootToParagraphs]),
      removeUnnecessaryBrTagsDeep,
      removeEmptyTags,
      ...(settings[PRETTIFY_OPTION.DISABLE_HANDLE_INLINE_STYLES]
        ? []
        : [handleInlineStylesDeep]),
      removeForbiddenTags,
      ...(settings[PRETTIFY_OPTION.DISABLE_RENDER] ? [] : [render])
    ];

    const debug = settings.debug || false;

    if (debug) {
      let currentValue = html;

      handlerList.forEach((handler) => {
        const handlerName = handler.name;
        console.time(handlerName);
        currentValue = handler(currentValue);
        console.timeEnd(handlerName);
        console.log(`${handlerName}: `, shallowFreeze(currentValue));
      });
    }

    return pipe(...handlerList)(html)
  } catch (err) {
    console.error(err);
    console.log('Original html is returned as a fallback');
    return html
  }
}

function shallowFreeze(value) {
  if (isString(value)) return value

  if (isArray(value)) return [...value]

  return { ...value }
}

function processImages({
  withCustomComponents,
  imageCustomComponent,
  aspectRatio,
  srcsetLocation,
  imageModifierFn,
  addImageSizeClass,
  debug = false
}) {
  return (html) => {
    if (!html) return html

    return html.replace(IMAGE_MATCH_REGEXP, (match) => {
      const { IMG } = SEARCH_INSIDE_IMAGE_HTML_REGEXP_STRING;
      const imgRegexp = new RegExp(IMG, 'gmis');
      const isImageFound = match.match(imgRegexp);

      if (!isImageFound) return match

      const settings = {
        debug,
        aspectRatio,
        prettifyFn: prettifyHtml,
        customImageTagName: withCustomComponents && imageCustomComponent,
        srcsetLocation,
        imageModifierFn,
        addSizeClass: addImageSizeClass
      };
      const imageHtml = new ImageHtml(match, settings);

      return imageHtml.getFinalImageHtml()
    })
  }
}

function createImageHtml({
  align,
  source,
  altText,
  width,
  renderWidth,
  height,
  caption,
  link,
  withCustomComponents,
  imageCustomComponent,
  aspectRatio,
  dataMediaId,
  log = false
}) {
  const imageData = {
    align,
    source,
    altText,
    width,
    renderWidth,
    height,
    caption,
    link,
    dataMediaId,
    aspectRatio
  };

  const settings = {
    log,
    aspectRatio,
    prettifyFn: prettifyHtml,
    customImageTagName: withCustomComponents && imageCustomComponent
  };
  const imageHtml = new ImageHtml(imageData, settings);

  return imageHtml.getFinalImageHtml()
}

function processImagesWrapper({
  withCustomComponents,
  imageCustomComponent,
  srcsetLocation,
  imageModifierFn,
  addImageSizeClass
}) {
  return processImages({
    withCustomComponents,
    imageCustomComponent,
    srcsetLocation,
    imageModifierFn,
    addImageSizeClass
  })
}

function sanitizeAttributes(elements) {
  if (isArray(elements)) {
    return elements.map((element) => sanitizeAttributes(element))
  }

  if (isObject(elements)) {
    return {
      ...elements,
      attrs: sanitizeAttrValues(elements.attrs),
      content: sanitizeAttributes(elements.content)
    }
  }

  return elements

  function sanitizeAttrValues(attrs) {
    if (!isObject(attrs)) return attrs

    return Object.entries(attrs).reduce((acc, [attrName, attrValue]) => {
      const isAttrNameForbidden = attrName && !!attrName.match(/^on/);

      if (isAttrNameForbidden) {
        return {
          ...acc
        }
      }

      return {
        ...acc,
        [attrName]: attrValue
      }
    }, {})
  }
}

function parseHtmlAndSanitizeAttributes(html) {
  return pipe(parseHtml, sanitizeAttributes, render)(html)
}

const TERM_CLASS = {
  MAIN: 'terms__main-term',
  SECONDARY: 'terms__secondary-term'
};

/**
 * HTML tag contains <termId + occurrence> (example: 7c7739f6-bc80-462f-ba3b-0e7fcf5534ee_1)
 * But we need only termId
 * @type {number}
 */
const TERM_ID_LENGTH = 36;

function checkIfParsedElementIsTerm(el) {
  if (!el || !el.attrs || !el.tag || el.tag !== HTML_TAG.LINK) return false

  const termId = el.attrs.id;
  const linkClass = el.attrs.class || [];

  return (
    linkClass &&
    termId &&
    [TERM_CLASS.MAIN, TERM_CLASS.SECONDARY].some((termClass) =>
      linkClass.includes(termClass)
    )
  )
}

function getTermIdFromParsedTerm(el) {
  return pathOr('', ['attrs', 'id'], el).slice(0, TERM_ID_LENGTH)
}

function processHtml(
  html,
  {
    allowScripts = false,
    prettify = true,
    decode = false,
    withCustomComponents = true,
    imageCustomComponent = CUSTOM_HTML_TAG.A_IMAGE,
    srcsetLocation,
    imageModifierFn,
    addImageSizeClass = false,
    scriptCustomComponent = CUSTOM_HTML_TAG.A_SCRIPT,
    iframeCustomComponent = CUSTOM_HTML_TAG.A_IFRAME,
    linkCustomComponent = CUSTOM_HTML_TAG.A_LINK,
    debug = false
  } = {}
) {
  if (typeof html !== 'string') {
    console.error(`processHTML: not valid argument type`);
    return ''
  }

  if (html === '') return ''

  const handlersSettings = generateHandlersSettings({
    iframeCustomComponent,
    scriptCustomComponent,
    linkCustomComponent,
    allowScripts,
    withCustomComponents
  });

  const prettifyHandler = (html) => prettifyHtml.bind(this, html, { debug })();

  const processHTMLHandlers = [
    processImagesWrapper({
      withCustomComponents,
      imageCustomComponent,
      srcsetLocation,
      imageModifierFn,
      addImageSizeClass
    }),
    ...(prettify ? [prettifyHandler] : []),
    parseHtml,
    generateParsedDomElementsHandlerDeep(handlersSettings),
    render,
    parseHtmlAndSanitizeAttributes,

    /**
     * Warning! Parsing of decoded HTML Will result in unexpected bugs.
     *
     * For example, given the following HTML:
     * <p>Text (1 &lt; 2) &amp; (1 &lt; 3) &lt;p&gt;Test&lt;/p&gt;</p>
     *
     * After decoding will be:
     * <p>Text (1 < 2) & (1 < 3) <p>Test</p></p>
     *
     * Parsing this result will lead to treating decoded elements as
     * REAL HTML (!)
     */
    ...(decode ? [decodeHTML] : [])
  ];

  if (!processHTMLHandlers.length) return html

  return pipe(...processHTMLHandlers)(html)
}

function checkIfParsedElementIsIframe(el) {
  return el && el.tag && el.tag === HTML_TAG.IFRAME
}

function checkIfParsedElementIsFigure(el) {
  return el && el.tag && el.tag === HTML_TAG.FIGURE
}

function checkIfParsedElementIsImage(el) {
  return el && el.tag && el.tag === HTML_TAG.IMAGE
}

function checkIfParsedElementIsFigcaption(el) {
  return el && el.tag && el.tag === HTML_TAG.FIGCAPTION
}

function checkIfParsedElementIsScript(el) {
  return el && el.tag && el.tag === HTML_TAG.SCRIPT
}

function checkIfParsedElementIsLink(el) {
  return (
    el && el.tag && el.tag === HTML_TAG.LINK && !checkIfParsedElementIsTerm(el)
  )
}

function generateHandlersSettings({
  iframeCustomComponent,
  scriptCustomComponent,
  linkCustomComponent,
  allowScripts,
  withCustomComponents
}) {
  function handleIframeElement(el) {
    el.tag = iframeCustomComponent;
    return el
  }

  function handleScriptElement(el) {
    el.tag = scriptCustomComponent;
    return el
  }

  function handleRemoveScriptElement(el) {
    return null
  }

  function handleLinkElement(el) {
    if (!el.attrs) {
      return {
        ...el,
        tag: linkCustomComponent,
        attrs: {}
      }
    }

    const to = el.attrs.href;
    const openInNewTab = el.attrs.target && el.attrs.target === TARGET.BLANK;
    const directive =
      LinkRelAttributeConverter.generateBackendEnumsFromHtmlRelString(
        el.attrs.rel
      ) || null;
    const props = {
      ...omit(['href', 'rel'], el.attrs),
      to,
      openInNewTab,
      directive
    };

    /**
     * Please note:
     * attrs are used for html-render library
     * props are used for Vue render function
     */
    return {
      ...el,
      tag: linkCustomComponent,
      attrs: props,
      props
    }
  }

  const customIframeHandlerSettings = {
    conditionFn: checkIfParsedElementIsIframe,
    handlerFn: handleIframeElement
  };

  const removeScriptHandlerSettings = {
    conditionFn: checkIfParsedElementIsScript,
    handlerFn: handleRemoveScriptElement
  };

  const customScriptHandlerSettings = {
    conditionFn: checkIfParsedElementIsScript,
    handlerFn: handleScriptElement
  };

  const customLinkHandlerSettings = {
    conditionFn: checkIfParsedElementIsLink,
    handlerFn: handleLinkElement
  };

  return [
    ...(withCustomComponents
      ? [customIframeHandlerSettings, customLinkHandlerSettings]
      : []),
    ...(!allowScripts ? [removeScriptHandlerSettings] : []),
    ...(allowScripts && withCustomComponents
      ? [customScriptHandlerSettings]
      : [])
  ]
}

function checkIfParsedElementIsTwitterCard(el) {
  if (!el || !el.attrs || !el.tag || el.tag !== HTML_TAG.BLOCKQUOTE) {
    return false
  }

  const blockquoteClass = el.attrs.class;
  const isContentExist =
    el.content && Array.isArray(el.content) && !!el.content.length;

  return blockquoteClass === TWITTER_CARD_CLASS_NAME && isContentExist
}

function getTwitterCardCount(parsedHtml) {
  let twitterCardCounter = 0;
  const handlerSettings = [
    {
      conditionFn: checkIfParsedElementIsTwitterCard,
      handlerFn: (el) => {
        twitterCardCounter++;

        return el
      }
    }
  ];
  const findTwitterCards = generateParsedDomElementsHandlerDeep(handlerSettings);
  findTwitterCards(parsedHtml);

  return twitterCardCounter
}
function addTwitterCardScript(html) {
  if (!html) return html

  try {
    const parsedHtml = parseHtml(html);
    const twitterCardCounter = getTwitterCardCount(parsedHtml);

    if (!twitterCardCounter) return html

    const scriptHandlerSettings = [
      {
        conditionFn: (el) => {
          if (!checkIfParsedElementIsScript(el)) return false

          const scriptSrc = el.attrs && el.attrs.src;

          return scriptSrc === TWITTER_CARD_SCRIPT_SOURCE
        },
        handlerFn: () => null
      }
    ];
    const removeTwitterScripts = generateParsedDomElementsHandlerDeep(
      scriptHandlerSettings
    );
    const parsedHtmlWithoutTwitterCardScripts = removeTwitterScripts(parsedHtml);
    const renderedHtml = render([
      ...parsedHtmlWithoutTwitterCardScripts,
      {
        tag: HTML_TAG.SCRIPT,
        attrs: {
          async: '',
          charset: 'utf-8',
          src: TWITTER_CARD_SCRIPT_SOURCE
        }
      }
    ]);
    return renderedHtml
  } catch (err) {
    return html
  }
}

function isTwitterCardEmbedCodeValid(embedCode) {
  if (!embedCode) return false

  try {
    const parsedHtml = parseHtml(processHtml(embedCode));

    return (
      parsedHtml.length === 1 &&
      checkIfParsedElementIsTwitterCard(parsedHtml[0])
    )
  } catch (err) {
    return false
  }
}

function processHtmlForPayload(html, options = {}) {
  return processHtml(html, {
    withCustomComponents: false,
    ...options
  })
}

export { ImageHtml, LinkRelAttributeConverter, PRETTIFY_OPTION, TERM_CLASS, addTwitterCardScript, checkIfParsedElementIsFigcaption, checkIfParsedElementIsFigure, checkIfParsedElementIsIframe, checkIfParsedElementIsImage, checkIfParsedElementIsLink, checkIfParsedElementIsScript, checkIfParsedElementIsTerm, checkIfParsedElementIsTwitterCard, checkIsElementIsVmlTag, createImageHtml, getTag, getTermIdFromParsedTerm, isElementPureText, isEmptyTagDeep, isTwitterCardEmbedCodeValid, parseHtml, prettifyHtml, processHtml, processHtmlForPayload, processImages, removeForbiddenTags };
